import { useEffect, useRef } from "react";
import { getStrapiMedia } from "../../utils/utils";
import "./HomepageInNumbers.scss";
import { InNumbersComponent_Plain } from "../../types/components/homepage/InNumbersComponent.ts";

const HomepageInNumbers = ({ data }: { data: InNumbersComponent_Plain }) => {
  const listRef = useRef<HTMLUListElement>(null); // Ref for the ul element containing list items
  const itemsRef = useRef<HTMLLIElement[]>([]); // Ref array for individual list items

  const initialPaddings = [
    13 * 12,
    21.6 * 12,
    35.6 * 12,
    51.6 * 12,
    68 * 12,
    78 * 12,
    85 * 12,
    85 * 12,
  ]; // First item: 5rem = 80px, others remain the same

  // Map slides and store refs for each item, setting initial styles
  const slides = data.numbers.map((item, i: number) => (
    <li
      className="numbers-inf-list__item"
      key={i}
      ref={(el) => {
        if (el) itemsRef.current[i] = el;
      }}
      style={{
        opacity: "0.15", // Set initial opacity to 0.15 for all items, including the first
        paddingLeft: `${initialPaddings[i]}px`, // Set initial padding-left for all items
      }}
    >
      <div className="numbers-inf-list__num">{item.title}</div>
      <div
        className="numbers-inf-list__text"
        dangerouslySetInnerHTML={{
          __html: item.text,
        }}
      ></div>
    </li>
  ));

  // useEffect for scroll-based animation (only downward scroll for now)
  useEffect(() => {
    const initialPaddings = [
      13 * 12,
      21.6 * 12,
      35.6 * 12,
      51.6 * 12,
      68 * 12,
      78 * 12,
      85 * 12,
      85 * 12,
    ];

    if (!listRef.current || itemsRef.current.length === 0) return;

    let animationFrameId: number | null = null;

    const updateAnimations = () => {
      const windowHeight = window.innerHeight;
      const currentScrollY = window.scrollY;

      itemsRef.current.forEach((item, index) => {
        if (!item) return;

        const itemRect = item.getBoundingClientRect();
        const itemTop = itemRect.top + currentScrollY;

        const isVisible = itemTop <= currentScrollY + windowHeight / 1.7;

        // Define initial and target values
        const initialOpacity = 0.15; // Initial opacity for all items (including first)
        const initialPaddingLeft = initialPaddings[index] || 0; // Initial padding-left from array, default to 0
        const targetOpacity = 1; // Target opacity for visible items
        const targetPaddingLeft = 0; // Target padding-left for visible items

        if (isVisible) {
          const visibilityProgress = Math.min(
            1,
            Math.max(
              0,
              (currentScrollY + windowHeight / 1.7 - itemTop) /
                (windowHeight / 1.7),
            ),
          );
          const opacityProgress = visibilityProgress; // Match opacity progress with padding-left for synchronized completion

          // When scrolling down, increase opacity to 1 and decrease padding-left to 0 (right to left)
          const newOpacity =
            initialOpacity + (targetOpacity - initialOpacity) * opacityProgress;
          const newPaddingLeft =
            initialPaddingLeft -
            (initialPaddingLeft - targetPaddingLeft) * visibilityProgress; // Ensure full decrease to 0
          item.style.opacity = newOpacity.toString(); // Ensure string for style
          item.style.paddingLeft = `${newPaddingLeft}px`; // Decrease padding-left to 0
        } else {
          // No reset to initial values when item goes out of viewport upward; keep current values
        }
      });
      animationFrameId = null;
    };

    // Start animation on scroll
    const handleScroll = () => {
      if (animationFrameId === null) {
        animationFrameId = requestAnimationFrame(updateAnimations);
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Add touchmove listener for mobile devices (inertia)
    window.addEventListener("touchmove", handleScroll, { passive: true });

    // Initial animation call
    updateAnimations();

    // Cleanup listeners and animation on unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("touchmove", handleScroll);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [data]); // Dependency on data for initialization after data loads

  return (
    <section className="numbers-inf">
      <div className="wrapper">
        <div className="block-title show-tab">{data.title}</div>
        <div className="numbers-inf__left">
          <div className="block-title">{data.title}</div>
          <div className="numbers-inf__bottom">
            <div className="numbers-inf__total">
              <div className="numbers-inf__total-text">{data.totalTitle}</div>
              <div className="numbers-inf__total-num">{data.totalNumber}</div>
            </div>
            <div className="numbers-inf__img">
              {data.image ? (
                <img
                  src={getStrapiMedia(data.image.url)}
                  width="384"
                  height="317"
                  alt={data.title}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                ""
              )}
              {data.image2 ? (
                <img
                  src={getStrapiMedia(data.image2.url)}
                  width="384"
                  height="317"
                  alt={data.title}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
        <div className="numbers-inf__right">
          <ul className="numbers-inf-list" ref={listRef}>
            {slides}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default HomepageInNumbers;
